import React from 'react';
import { VerticalStepper, Box, Grid, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';

import { PsBeaconButton } from '../BeaconRegister';
import { Content } from '../Content';
import { dispatchOverlayLoad } from '../Analytics';
import { dateFormatter, log, setLastRequestTime, showModal } from '../../../Utils';
import { axiosDocumentInstance } from '../../../actions/AxiosInstance';
import api from '../../../actions/EndPoints';
import HubPageBase from '../../HubPages/HubPageBase';
import { APPLICATION_PDF, DATE_FORMAT_MM_DD_YYYY } from '../../../Constants';

const PLDLPage = ({ state, dispatch }) => {
    const { apiKey, isEdeliveryConsented } = state;
    const externalGroupId = state?.planningGroup?.externalId;
    const [serviceError, setServiceError] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [buttonSize, setButtonSize] = React.useState(
        window.innerWidth <= 600 ? '100%' : 'inherit'
    );
    const smallScreenSize = window.matchMedia('(max-width: 600px)');

    const popupAlertText = Content.planBlockedAlert.contentText;
    const popupAlertTitle = Content.planBlockedAlert.contentTitle;
    const getTitleText =
        state.releasedStrategyComparison || state.releasedSelectedStrategy
            ? 'Your plan documents are ready!'
            : 'Your personalized financial plan is ready!';

    const getButtonText = isEdeliveryConsented ? 'View Plan (PDF)' : 'Accept Terms of Service';

    smallScreenSize.addEventListener('change', function () {
        setButtonSize(smallScreenSize.matches ? '100%' : 'inherit');
    });

    const openEDeliveryModal = () => {
        showModal('e-delivery')
        dispatchOverlayLoad('E Delivery Consent Modal');
    };

    const getSteps = [
        {
            label: 'Accept the Terms of Service',
            description: ``,
            completed: isEdeliveryConsented,
            active: !isEdeliveryConsented,
        },
        {
            label: 'Review your financial plan',
            description: ``,
            completed: false,
            active: isEdeliveryConsented,
        },
    ];

    async function retrievePlan() {
        log(console.log('Calling retrieve plan'));
        setServiceError(false);
        try {
            const getPlan = await axiosDocumentInstance.get(
                `${api.getLatestPlanAPI(externalGroupId)}?docType=FINANCIAL_PLAN`,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        apikey: `${apiKey}`,
                        Accept: APPLICATION_PDF,
                    },
                }
            );

            if (getPlan) {
                const { data, headers } = getPlan;
                setLastRequestTime();
                const lastModifiedTimeStamp = headers['last-modified'];
                if (data !== undefined) {
                    const planBlob = new Blob([data], { type: APPLICATION_PDF });
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        // IE and Edge browser use msSaveOrOpenBlob
                        window.navigator.msSaveOrOpenBlob(
                            planBlob,
                            `Personalized_Plan_${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT_MM_DD_YYYY
                            )}.pdf`
                        );
                    } else {
                        try {
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(planBlob);
                            link.download = `Personalized_Plan_${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT_MM_DD_YYYY
                            )}.pdf`;
                            link.click();
                            setTimeout(() => {
                                window.URL.revokeObjectURL(data);
                            }, 100);
                        } catch (e) {
                            log(console.log(e));
                            setServiceError(true);
                        }
                    }
                }
            }
        } catch (err) {
            setServiceError(true);
            setLastRequestTime();
            console.log(err);
        }
    }

    async function retrieveRICMPlan(docType) {
        log(console.log('Calling retrieve RICM plan: ' + docType));
        setServiceError(false);
        try {
            const getPlan = await axiosDocumentInstance.get(
                `${api.getLatestPlanAPI(externalGroupId)}?docType=${docType}`,
                {
                    responseType: 'arraybuffer',
                    headers: {
                        apikey: `${apiKey}`,
                        Accept: APPLICATION_PDF,
                    },
                }
            );

            if (getPlan) {
                const { data, headers } = getPlan;
                setLastRequestTime();
                const lastModifiedTimeStamp = headers['last-modified'];
                if (data !== undefined) {
                    const planBlob = new Blob([data], { type: APPLICATION_PDF });
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        // IE and Edge browser use msSaveOrOpenBlob
                        window.navigator.msSaveOrOpenBlob(
                            planBlob,
                            `Personalized_Plan__${docType}__${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT_MM_DD_YYYY
                            )}.pdf`
                        );
                    } else {
                        try {
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(planBlob);
                            link.download = `Personalized_Plan_RICM__${docType}__${dateFormatter(
                                lastModifiedTimeStamp,
                                DATE_FORMAT_MM_DD_YYYY
                            )}.pdf`;
                            link.click();
                            setTimeout(() => {
                                window.URL.revokeObjectURL(data);
                            }, 100);
                        } catch (e) {
                            log(console.log(e));
                            setServiceError(true);
                        }
                    }
                }
            }
        } catch (err) {
            setServiceError(true);
            setLastRequestTime();
            console.log(err);
        }
    }

    const acceptTermsOnClick = () => {
        if (isEdeliveryConsented) {
            retrievePlan();
        } else {
            openEDeliveryModal();
        }
    };

    const ricmPlansOnClick = () => {
        if (isEdeliveryConsented) {
            if (state.releasedStrategyComparison) {
                retrieveRICMPlan('STRATEGY_COMPARISON');
            }
            if (state.releasedSelectedStrategy) {
                retrieveRICMPlan('SELECTED_STRATEGY');
            }
        } else {
            dispatch({
                type: 'RICM_PLAN_CLICK',
                ricmPlanClicked: true,
            });
            openEDeliveryModal();
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <HubPageBase
            state={state}
            dispatch={dispatch}
            serviceError={serviceError}
            popUpError={open}
            popupAlertTitle={popupAlertTitle}
            popupAlertText={popupAlertText}
            popupClose={handleClose}>
            <Grid
                container
                className="b-container-content"
                sx={{ inlineSize: 'unset !important', width: '100%', paddingTop: '64px' }}>
                <Grid item cols={12} lg={8}>
                    <Typography
                        sx={{ paddingBottom: '16px' }}
                        type="headline" serif loose scale={400}>
                        {getTitleText}
                    </Typography>
                    <Typography
                        sx={{ paddingBottom: '48px' }}
                        type="body" loose scale={200} light>
                        {state.releasedStrategyComparison ||
                            state.releasedSelectedStrategy ? (
                            <Box>
                                <Box sx={{ paddingBottom: '72px' }}>
                                    {Content.PLDLPageContent.planDocuments.content}
                                </Box>
                                <Typography
                                    sx={{ paddingBottom: '16px' }}
                                    type="headline" scale={100} bold>
                                    Financial Plan
                                </Typography>
                                <Box sx={{ paddingBottom: '48px' }}>
                                    Your custom plan includes advice from our team of experts on
                                    financial planning, asset allocation, and investment
                                    recommendations.
                                </Box>
                                {!isEdeliveryConsented && (
                                    <Box>
                                        {Content.PLDLPageContent.financialPlan.acceptTerms}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ paddingBottom: '48px' }}>
                                    {Content.PLDLPageContent.financialPlan.content}
                                </Box>
                                {!isEdeliveryConsented && (
                                    <Box>
                                        {Content.PLDLPageContent.financialPlan.acceptTerms}
                                    </Box>
                                )}
                            </Box>
                        )}
                        <Box sx={{ paddingTop: '24px' }}>
                            <PsBeaconButton
                                style={{ width: buttonSize }}
                                variant="primary"
                                scale="jumbo"
                                iconAlign="left"
                                onClick={acceptTermsOnClick}>
                                {getButtonText}
                            </PsBeaconButton>
                        </Box>
                        {(state.releasedStrategyComparison ||
                            state.releasedSelectedStrategy) && (
                                <Box>
                                    <Typography
                                        sx={{ paddingTop: '72px', paddingBottom: '16px' }}
                                        type="headline" scale={100} bold>
                                        Retirement Income Plan
                                    </Typography>
                                    <Box>{Content.PLDLPageContent.planDocuments.subContent}</Box>
                                    <Box sx={{ paddingTop: '24px' }}>
                                        <PsBeaconButton
                                            style={{ width: buttonSize }}
                                            variant="secondary"
                                            scale="jumbo"
                                            iconAlign="left"
                                            onClick={ricmPlansOnClick}>
                                            View Retirement Income Plan (PDF)
                                        </PsBeaconButton>
                                    </Box>
                                </Box>
                            )}
                    </Typography>
                </Grid>
                <Grid cols={12} lg={4} item sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <VerticalStepper steps={getSteps} />
                    </Box>
                </Grid>
            </Grid>
        </HubPageBase>
    );
};

export default PLDLPage;
